import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ArrowBack,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface LetterHintPricing {
  id: string;
  singleHintPrice: number;
  threeHintPrice: number;
  fiveHintPrice: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

const LetterHintPricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState<LetterHintPricing | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    singleHintPrice: 15,
    threeHintPrice: 40,
    fiveHintPrice: 60,
    isActive: true,
  });

  useEffect(() => {
    loadPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  const loadPricing = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/letter-hint-pricing`,
        getAuthHeaders()
      );

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setPricing(data);
        setFormData({
          singleHintPrice: data.singleHintPrice,
          threeHintPrice: data.threeHintPrice,
          fiveHintPrice: data.fiveHintPrice,
          isActive: data.isActive,
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/letter-hint-pricing`,
        formData,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess('Fiyatlandırma güncellendi!');
        loadPricing();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Güncelleme sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const calculateSavings = () => {
    const threeSavings = ((formData.singleHintPrice * 3) - formData.threeHintPrice);
    const threeSavingsPercent = (threeSavings / (formData.singleHintPrice * 3) * 100).toFixed(0);

    const fiveSavings = ((formData.singleHintPrice * 5) - formData.fiveHintPrice);
    const fiveSavingsPercent = (fiveSavings / (formData.singleHintPrice * 5) * 100).toFixed(0);

    return {
      threeSavings,
      threeSavingsPercent,
      fiveSavings,
      fiveSavingsPercent,
    };
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const savings = calculateSavings();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/admin/dashboard')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            💡 Harf İpucu Fiyatlandırması
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Alert severity="info" sx={{ mb: 3 }}>
          Bu fiyatlar iOS app'te dinamik olarak çekilir. Değiştirdiğinizde tüm kullanıcılar yeni fiyatları görür.
        </Alert>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Pricing Form */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Fiyat Ayarları
            </Typography>

            <TextField
              fullWidth
              margin="normal"
              type="number"
              label="1 İpucu"
              value={formData.singleHintPrice}
              onChange={(e) => setFormData({ ...formData, singleHintPrice: parseInt(e.target.value) })}
              InputProps={{
                endAdornment: '☕ Bean',
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              type="number"
              label="3 İpucu Paketi"
              value={formData.threeHintPrice}
              onChange={(e) => setFormData({ ...formData, threeHintPrice: parseInt(e.target.value) })}
              InputProps={{
                endAdornment: '☕ Bean',
              }}
              helperText={
                savings.threeSavings > 0
                  ? `%${savings.threeSavingsPercent} indirim (${savings.threeSavings} bean tasarruf)`
                  : 'İndirim yok'
              }
            />

            <TextField
              fullWidth
              margin="normal"
              type="number"
              label="5 İpucu Paketi (Önerilen)"
              value={formData.fiveHintPrice}
              onChange={(e) => setFormData({ ...formData, fiveHintPrice: parseInt(e.target.value) })}
              InputProps={{
                endAdornment: '☕ Bean',
              }}
              helperText={
                savings.fiveSavings > 0
                  ? `%${savings.fiveSavingsPercent} indirim (${savings.fiveSavings} bean tasarruf)`
                  : 'İndirim yok'
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Aktif"
            />

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadPricing}
              >
                Yenile
              </Button>
            </Box>
          </Paper>

          {/* Preview Cards */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Önizleme (Kullanıcı Görünümü)
            </Typography>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6">💡 1 İpucu</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Tek harf aç
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h5">☕ {formData.singleHintPrice}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Bean
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 2, border: '1px solid', borderColor: 'primary.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6">💡 3 İpucu</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {savings.threeSavings > 0 && (
                        <span style={{ color: 'green' }}>%{savings.threeSavingsPercent} tasarruf</span>
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h5">☕ {formData.threeHintPrice}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Bean
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ border: '2px solid', borderColor: 'warning.main', position: 'relative' }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  right: 12,
                  backgroundColor: 'warning.main',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                }}
              >
                ÖNERİLEN
              </Box>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6">💡 5 İpucu</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {savings.fiveSavings > 0 && (
                        <span style={{ color: 'green' }}>%{savings.fiveSavingsPercent} tasarruf</span>
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h5">☕ {formData.fiveHintPrice}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Bean
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {pricing && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" color="textSecondary">
                  Son Güncelleme: {new Date(pricing.updatedAt).toLocaleString('tr-TR')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LetterHintPricingPage;
